import { AggregatedPositionDTO, PositionDTO } from '@tradingblock/api';
import _ from 'lodash';
import { useMemo } from 'react';
import { SymbolPositions } from '../../../types';
import { subAccountSelectors } from '../../../data/global/selectors/subAccountSelectors';
import { useStateSelector } from '../../../data/global/dataSelectors';

export default function useAllAggregatedPositions(
  symbolPositions: SymbolPositions,
  combineSameSymbols: boolean = true,
  subAccountId?: number
) {
  const masterSubAccountId = useStateSelector(subAccountSelectors.getMasterSubAccountId);

  return useMemo<AggregatedPositionDTO[]>(() => {
    // First get all positions
    const allPositions = Object.values(symbolPositions).reduce<PositionDTO[]>(
      (acc, symbolPosition) => [...acc, ...symbolPosition.positions],
      []
    );

    // Filter based on subAccountId, including -1 positions for master account
    const filteredPositions =
      !subAccountId || subAccountId === -1
        ? allPositions
        : allPositions.filter(p =>
            subAccountId === masterSubAccountId
              ? p.SubaccountId === subAccountId || p.SubaccountId === -1
              : p.SubaccountId === subAccountId
          );

    // Group filtered positions by symbol and continue with aggregation
    const positionsBySymbol = _.groupBy(filteredPositions, 'UnderlyingSymbol');

    console.log({ filteredPositions, positionsBySymbol });

    return Object.entries(positionsBySymbol)
      .reduce<AggregatedPositionDTO[]>((acc, [symbol, positions]) => {
        const aggregatedForSymbol = positions.reduce(
          (alteredPositions: AggregatedPositionDTO[], position: PositionDTO) => {
            const positionValueBasis = position.OpenQuantity * position.OptionMultiplier;

            if (combineSameSymbols) {
              const currentCombinedPositionIndex = alteredPositions.findIndex(
                combinedPosition => combinedPosition.Symbol === position.Symbol
              );

              if (currentCombinedPositionIndex !== -1) {
                if (alteredPositions[currentCombinedPositionIndex].SubaccountId !== position.SubaccountId) {
                  const isMasterAndUnassigned =
                    (alteredPositions[currentCombinedPositionIndex].SubaccountId === masterSubAccountId &&
                      position.SubaccountId === -1) ||
                    (alteredPositions[currentCombinedPositionIndex].SubaccountId === -1 &&
                      position.SubaccountId === masterSubAccountId);

                  if (!isMasterAndUnassigned) {
                    return [
                      ...alteredPositions,
                      { ...position, ValueBasis: positionValueBasis, PositionIds: [position.PositionId] },
                    ];
                  }
                }

                if (alteredPositions[currentCombinedPositionIndex].PositionIds.length <= 1) {
                  alteredPositions[currentCombinedPositionIndex] = {
                    ...alteredPositions[currentCombinedPositionIndex],
                    OpenPrice:
                      alteredPositions[currentCombinedPositionIndex].OpenPrice *
                      alteredPositions[currentCombinedPositionIndex].OpenQuantity,
                  };
                }

                alteredPositions[currentCombinedPositionIndex] = {
                  ...alteredPositions[currentCombinedPositionIndex],
                  OpenQuantity: alteredPositions[currentCombinedPositionIndex].OpenQuantity + position.OpenQuantity,
                  CostBasis: alteredPositions[currentCombinedPositionIndex].CostBasis + position.CostBasis,
                  ValueBasis: alteredPositions[currentCombinedPositionIndex].ValueBasis + positionValueBasis,
                  OpenPrice:
                    alteredPositions[currentCombinedPositionIndex].OpenPrice +
                    position.OpenPrice * position.OpenQuantity,
                  PositionIds: [...alteredPositions[currentCombinedPositionIndex].PositionIds, position.PositionId],
                  Commission: alteredPositions[currentCombinedPositionIndex].Commission + position.Commission,
                };

                return alteredPositions;
              }
            }

            return [
              ...alteredPositions,
              { ...position, ValueBasis: positionValueBasis, PositionIds: [position.PositionId] },
            ];
          },
          []
        );

        return [...acc, ...aggregatedForSymbol];
      }, [])
      .map(positions => {
        if (combineSameSymbols && positions.PositionIds.length > 1) {
          return {
            ...positions,
            OpenPrice: positions.OpenPrice / positions.OpenQuantity,
          };
        }
        return positions;
      });
  }, [symbolPositions, combineSameSymbols, subAccountId]);
}
